import React, { useState, useEffect } from 'react';
import { MessageSquare, Trash2, Edit, Download, Archive, MoveRight, Loader, ChevronRight, ChevronDown, RefreshCw, ChevronLeft, Video, Cloud, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useStore } from '../store';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { FeedbackModal } from './FeedbackModal';
import { EditVideoModal } from './EditVideoModal';
import { BulkReleaseAssignmentModal } from './BulkReleaseAssignmentModal';
import { ConfirmationModal } from './ConfirmationModal';
import { AdTagIcon } from './icons/AdTagIcon';
import { formatDateToDDMMYY } from '../utils/dateFormat';
import { supabase } from '../lib/supabase';
import type { VideoSubmission } from '../types';

interface VideoListProps {
  artistId?: string;
  isArtistView?: boolean;
  isGenerating?: boolean;
  filters?: {
    artistId?: string;
    type?: string;
    status?: string;
  };
  selectedVideo?: VideoSubmission | null;
  onSelectedVideo?: (video: VideoSubmission | null) => void;
}

export const VideoList: React.FC<VideoListProps> = ({ artistId, filters = {}, isArtistView = false, isGenerating = false, selectedVideo: externalSelectedVideo, onSelectedVideo }) => {
  const [internalSelectedVideo, setInternalSelectedVideo] = useState<VideoSubmission | null>(null);
  const selectedVideo = externalSelectedVideo !== undefined ? externalSelectedVideo : internalSelectedVideo;
  const setSelectedVideo = onSelectedVideo || setInternalSelectedVideo;
  const [editingVideo, setEditingVideo] = useState<VideoSubmission | null>(null);
  const [downloadingVideos, setDownloadingVideos] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [showBulkReleaseModal, setShowBulkReleaseModal] = useState(false);
  const [releaseCounts, setReleaseCounts] = useState<Record<string, number>>({});
  
  const {
    submissions,
    artists,
    deleteSubmission,
    updateSubmission,
    adCreatives,
    handleMoveToAdCreatives,
    fetchSubmissions,
    submissionsPagination
  } = useStore();
  const { client: artistClient, isProtected } = useArtistClient();
  
  // Ad creatives are loaded on-demand when needed (e.g., when moving videos)

  // Fetch total counts per release for this artist
  useEffect(() => {
    const fetchReleaseCounts = async () => {
      if (!artistId) return;
      
      try {
        // Get counts grouped by release_id for this artist
        const { data, error } = await (isProtected ? artistClient : supabase)
          .from('submissions')
          .select('release_id')
          .eq('artist_id', artistId as any)
          .neq('status' as any, 'archived' as any);
        
        if (error) throw error;
        
        // Count submissions per release
        const counts: Record<string, number> = {};
        (data || []).forEach((item: any) => {
          const releaseId = item.release_id || 'no-release';
          counts[releaseId] = (counts[releaseId] || 0) + 1;
        });
        
        setReleaseCounts(counts);
      } catch (err) {
        console.error('Error fetching release counts:', err);
      }
    };
    
    fetchReleaseCounts();
  }, [artistId]);
  
  // Define status order for default sorting
  const statusOrder = {
    'correction-needed': 0,
    'new': 1,
    'feedback-needed': 2,
    'ready': 3,
    'planned': 4,
    'posted': 5,
    'archived': 6
  };

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    videoId: string | null;
    videoName: string;
  }>({
    isOpen: false,
    videoId: null,
    videoName: '',
  });
  const [multiDeleteModal, setMultiDeleteModal] = useState<{ isOpen: boolean; count: number }>({ isOpen: false, count: 0 });
  const [multiDeleteConfirmInput, setMultiDeleteConfirmInput] = useState('');
  const [isMultiDeleting, setIsMultiDeleting] = useState(false);
  const [movingVideos, setMovingVideos] = useState<Set<string>>(new Set());
  const [showBulkStatusMenu, setShowBulkStatusMenu] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const getArtistName = (artistId: string | number) => {
    const artist = artists.find(a => a.id === artistId);
    return artist?.name || 'Unknown Artist';
  };

  const handleArchive = async (submission: VideoSubmission) => {
    try {
      // Update submission status
      await updateSubmission(
        submission.id.toString(),
        {
          status: submission.status === 'archived' ? 'new' : 'archived'
        },
        true, // Skip WhatsApp notification
        isProtected ? artistClient : undefined
      );

      // Refresh submissions to update the list (especially for artist view)
      if (isArtistView && artistId) {
        await fetchSubmissions(1, 1000, { artistId, includeArchived: true }, true, isProtected ? artistClient : undefined);
      } else if (filters) {
        await fetchSubmissions(
          submissionsPagination.currentPage,
          submissionsPagination.pageSize,
          filters,
          true
        );
      }
    } catch (error) {
      console.error('Failed to archive video:', error);
    }
  };

  const handleDownload = async (videoUrl: string) => {
    setDownloadingVideos(prev => new Set(prev).add(videoUrl));

    let filename = 'video.mp4';
    try {
      const urlPath = new URL(videoUrl).pathname;
      const lastPart = urlPath.split('/').pop();
      if (lastPart && lastPart.includes('.')) filename = decodeURIComponent(lastPart);
    } catch {
      // Keep the default filename when the URL cannot be parsed.
    }

    const processedUrl = videoUrl.includes('dropbox.com')
      ? (() => {
          const url = new URL(videoUrl);
          url.searchParams.set('dl', '1');
          return url.toString();
        })()
      : videoUrl;

    try {
      // Match Ad Creatives: fetch the source as a blob and trigger an
      // immediate browser download instead of opening the video in a tab.
      let response = await fetch(processedUrl);

      // Dropbox and non-CORS sources can use the force-download fallback.
      if (!response.ok) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const forceDownloadUrl = `${supabaseUrl}/functions/v1/force-download?url=${encodeURIComponent(videoUrl)}&filename=${encodeURIComponent(filename)}`;
        response = await fetch(forceDownloadUrl, {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        });
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (error) {
      console.error('Video download failed:', error);
      // Last fallback for sources that do not permit browser fetching.
      window.open(processedUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingVideos(prev => {
        const next = new Set(prev);
        next.delete(videoUrl);
        return next;
      });
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation.videoId) {
      try {
        await deleteSubmission(deleteConfirmation.videoId, isProtected ? artistClient : undefined);
      } catch (error) {
        console.error('Failed to delete video:', error);
      }
    }
    setDeleteConfirmation({
      isOpen: false,
      videoId: null,
      videoName: '',
    });
  };

  const handleMultiDelete = async () => {
    if (selectedVideos.size === 0) return;
    
    try {
      setIsMultiDeleting(true);
      const deletePromises = Array.from(selectedVideos).map(id => deleteSubmission(id, isProtected ? artistClient : undefined));
      await Promise.all(deletePromises);
      setSelectedVideos(new Set());
    } catch (error) {
      console.error('Failed to delete videos:', error);
    } finally {
      setIsMultiDeleting(false);
      setMultiDeleteModal({ isOpen: false, count: 0 });
      setMultiDeleteConfirmInput('');
    }
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedVideos.size === 0) return;
    
    try {
      setIsBulkUpdating(true);
      setShowBulkStatusMenu(false);
      
      const ids = Array.from(selectedVideos);
      
      // Update in batches of 100
      for (let i = 0; i < ids.length; i += 100) {
        const batch = ids.slice(i, i + 100);
        const { error } = await (isProtected ? artistClient : supabase)
          .from('submissions')
          .update({ status: newStatus, updated_at: new Date().toISOString() } as any)
          .in('id' as any, batch as any);
        
        if (error) throw error;
      }
      
      toast.success(`${ids.length} video${ids.length > 1 ? 's' : ''} updated to ${newStatus.replace(/-/g, ' ')}`);
      setSelectedVideos(new Set());
      
      // Refresh submissions
      if (isArtistView && artistId) {
        await fetchSubmissions(1, 1000, { artistId, includeArchived: true }, true, isProtected ? artistClient : undefined);
      } else if (filters) {
        await fetchSubmissions(
          submissionsPagination.currentPage,
          submissionsPagination.pageSize,
          filters,
          true
        );
      }
    } catch (error) {
      console.error('Failed to bulk update status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const onMoveToAdCreatives = async (submission: VideoSubmission) => {
    try {
      // Add video to moving state
      const submissionId = submission.id.toString();
      setMovingVideos(prev => new Set(prev).add(submissionId));
      
      // Use the store method to handle the move
      await handleMoveToAdCreatives(submission, isProtected ? artistClient : undefined);
      
      // Remove from moving state
      setMovingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    } catch (error) {
      console.error('Error moving video to ad creatives:', error);
      // Remove from moving state
      const submissionId = submission.id.toString();
      setMovingVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  // Toggle selection with shift-click support
  const toggleSelectVideo = (id: string, index: number, shiftKey: boolean = false) => {
    const newSelected = new Set(selectedVideos);
    
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      for (let i = start; i <= end; i++) {
        newSelected.add(String(sortedSubmissions[i].id));
      }
    } else {
      // Normal click: toggle single item
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
    }
    
    setSelectedVideos(newSelected);
    setLastSelectedIndex(index);
  };

  // Filter submissions based on all criteria including archived status
  const isVideoInAdCreatives = (videoUrl: string) => {
    return adCreatives.some(creative => creative.content === videoUrl);
  };

  const filteredSubmissions = submissions.filter((sub) => {
    // Apply artist filter if provided (both artistId prop and filters.artistId)
    // Use string comparison to handle type mismatches
    if (artistId && String(sub.artistId) !== String(artistId)) {
      return false;
    }
    
    if (filters.artistId && String(sub.artistId) !== String(filters.artistId)) {
      return false;
    }
    
    // For both artist and admin view:
    // Note: archived videos are already filtered out in the database query (store.ts)
    // unless explicitly filtered for them, so we don't need to filter them again here
    
    // Apply type and status filters if provided
    if (filters.type && sub.type !== filters.type) {
      return false;
    }
    
    if (filters.status && sub.status !== filters.status) {
      return false;
    }
    
    return true;
  });
  
  // Sort submissions by status (if no filters) or date
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    // If no filters are applied, sort by status order
    if (!filters.type && !filters.status && artistId) {
      const statusCompare = (statusOrder[a.status] ?? 999) - (statusOrder[b.status] ?? 999);
      if (statusCompare !== 0) return statusCompare;
      
      // If status is the same, sort by date within each status group
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Always newest first for now
    }
    
    // Otherwise sort by date as before
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA; // Always newest first for now
  });

  const getStatusBadgeClass = (status: VideoSubmission['status']) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'feedback-needed':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'correction-needed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'ready':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200';
      case 'planned':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'posted':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: VideoSubmission['status']) => {
    switch (status) {
      case 'new':
        return 'New';
      case 'feedback-needed':
        return 'Feedback Needed';
      case 'correction-needed':
        return 'Correction Needed';
      case 'ready':
        return 'Ready';
      case 'planned':
        return 'Planned';
      case 'posted':
        return 'Posted';
      case 'archived':
        return 'Archived';
      default:
        return status;
    }
  };

  const getStatusHeaderClass = (status: VideoSubmission['status']) => {
    switch (status) {
      case 'new':
        return 'bg-blue-500/20';
      case 'feedback-needed':
        return 'bg-yellow-500/20';
      case 'correction-needed':
        return 'bg-red-500/20';
      case 'ready':
        return 'bg-emerald-500/20';
      case 'planned':
        return 'bg-green-500/20';
      case 'posted':
        return 'bg-purple-500/20';
      case 'archived':
        return 'bg-gray-500/20';
      default:
        return 'bg-gray-500/20';
    }
  };

  const getStatusTextColor = (status: VideoSubmission['status']) => {
    switch (status) {
      case 'new':
        return 'text-blue-300';
      case 'feedback-needed':
        return 'text-yellow-300';
      case 'correction-needed':
        return 'text-red-300';
      case 'ready':
        return 'text-emerald-300';
      case 'planned':
        return 'text-green-300';
      case 'posted':
        return 'text-purple-300';
      case 'archived':
        return 'text-gray-300';
      default:
        return 'text-gray-300';
    }
  };

  // Check if any submission has a release assigned
  const hasAnyRelease = sortedSubmissions.some(sub => sub.release_id);

  // Group submissions by release first, then by status within each release
  const releaseGroups = sortedSubmissions.reduce((groups, submission) => {
    const releaseId = submission.release_id || 'no-release';
    const releaseName = submission.releases?.name || 'No Release Assigned';
    
    if (!groups[releaseId]) {
      groups[releaseId] = {
        releaseId,
        releaseName,
        byStatus: {} as Record<string, VideoSubmission[]>,
        total: 0
      };
    }
    
    const status = submission.status;
    if (!groups[releaseId].byStatus[status]) {
      groups[releaseId].byStatus[status] = [];
    }
    groups[releaseId].byStatus[status].push(submission);
    groups[releaseId].total++;
    
    return groups;
  }, {} as Record<string, { releaseId: string; releaseName: string; byStatus: Record<string, VideoSubmission[]>; total: number }>);

  // Sort release groups: releases with "new" or "feedback-needed" submissions first,
  // then other named releases alphabetically, then "No Release Assigned" last.
  const hasActionableStatus = (group: { byStatus: Record<string, VideoSubmission[]> }) =>
    (group.byStatus['new']?.length || 0) > 0 || (group.byStatus['feedback-needed']?.length || 0) > 0;

  const sortedReleaseGroups = Object.values(releaseGroups).sort((a, b) => {
    // "No Release Assigned" always last
    if (a.releaseId === 'no-release') return 1;
    if (b.releaseId === 'no-release') return -1;

    // Releases with new/feedback-needed submissions come first
    const aActionable = hasActionableStatus(a);
    const bActionable = hasActionableStatus(b);
    if (aActionable && !bActionable) return -1;
    if (!aActionable && bActionable) return 1;

    // Within the same priority group, sort alphabetically
    return a.releaseName.localeCompare(b.releaseName);
  });

  // Legacy grouping for when there are no releases (fallback to status-only grouping)
  const groupedSubmissions = sortedSubmissions.reduce((groups, submission) => {
    const status = submission.status;
    if (!groups[status]) {
      groups[status] = [];
    }
    groups[status].push(submission);
    return groups;
  }, {} as Record<string, VideoSubmission[]>);

  // Get ordered status keys that have videos (excluding archived)
  const orderedStatusKeys = (Object.keys(statusOrder) as VideoSubmission['status'][])
    .filter(status => status !== 'archived' && groupedSubmissions[status]?.length > 0)
    .sort((a, b) => statusOrder[a] - statusOrder[b]);

  // Get archived videos separately
  const archivedVideos = groupedSubmissions['archived'] || [];
  const hasArchivedVideos = archivedVideos.length > 0;

  // Safety check for submissions array
  if (!Array.isArray(submissions)) {
    console.error('VideoList: submissions is not an array', submissions);
    return (
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-6 text-center">
        <p className="text-gray-400">Unable to load video submissions. Please refresh the page.</p>
      </div>
    );
  }

  // Get selected submissions for bulk operations
  const selectedSubmissions = sortedSubmissions.filter(s => selectedVideos.has(String(s.id)));

  return (
    <>
    {/* Bulk Action Bar - shown when videos are selected */}
    {selectedVideos.size > 0 && (
      <div className="bg-blue-900 border-b border-blue-800 px-6 py-3 flex items-center justify-between">
        <span className="text-sm font-medium text-white">
          {selectedVideos.size} video{selectedVideos.size > 1 ? 's' : ''} selected
        </span>
        <div className="flex items-center gap-2">
          {artistId && (
            <button
              onClick={() => setShowBulkReleaseModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-sm font-medium"
            >
              <Edit className="h-4 w-4" />
              Assign Selected
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setShowBulkStatusMenu(!showBulkStatusMenu)}
              disabled={isBulkUpdating}
              className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors text-sm font-medium"
            >
              {isBulkUpdating ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Change Status
            </button>
            {showBulkStatusMenu && (
              <div className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 min-w-[180px]">
                {[
                  { value: 'new', label: 'New' },
                  { value: 'feedback-needed', label: 'Feedback Needed' },
                  { value: 'correction-needed', label: 'Correction Needed' },
                  { value: 'ready', label: 'Ready' },
                  { value: 'planned', label: 'Planned' },
                  { value: 'posted', label: 'Posted' },
                  { value: 'archived', label: 'Archived' },
                ].map((status) => (
                  <button
                    key={status.value}
                    onClick={() => handleBulkStatusChange(status.value)}
                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setMultiDeleteModal({ isOpen: true, count: selectedVideos.size })}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors text-sm font-medium"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      </div>
    )}

    <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12">
                <input
                  type="checkbox"
                  checked={filteredSubmissions.length > 0 && selectedVideos.size === filteredSubmissions.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedVideos(new Set(filteredSubmissions.map(s => String(s.id))));
                    } else {
                      setSelectedVideos(new Set());
                    }
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Video Name
              </th>
              {!artistId && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Artist
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Content Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Uploaded At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Updated At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {filteredSubmissions.length === 0 ? (
            <tr>
              <td colSpan={artistId ? 7 : 8} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                No video submissions found
              </td>
            </tr>
          ) : hasAnyRelease ? (
            /* Release-first grouping when artist has releases */
            sortedReleaseGroups.map((releaseGroup) => {
              // Get ordered status keys for this release group (excluding archived)
              const releaseStatusKeys = (Object.keys(statusOrder) as VideoSubmission['status'][])
                .filter(status => status !== 'archived' && releaseGroup.byStatus[status]?.length > 0)
                .sort((a, b) => statusOrder[a] - statusOrder[b]);
              
              // Get archived videos for this release
              const releaseArchivedVideos = releaseGroup.byStatus['archived'] || [];

              // Skip release group entirely if artist view and all videos are archived
              if (isArtistView && releaseStatusKeys.length === 0) return null;
              
              return (
                <React.Fragment key={releaseGroup.releaseId}>
                  {/* Release Header */}
                  <tr>
                    <td colSpan={artistId ? 7 : 8} className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                      <div className="flex items-center gap-3 text-sm font-medium text-white">
                        {(() => {
                          const releaseVideoIds = Object.values(releaseGroup.byStatus).flat().map(s => String(s.id));
                          const allSelected = releaseVideoIds.length > 0 && releaseVideoIds.every(id => selectedVideos.has(id));
                          return (
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={(e) => {
                                const newSelected = new Set(selectedVideos);
                                if (e.target.checked) {
                                  releaseVideoIds.forEach(id => newSelected.add(id));
                                } else {
                                  releaseVideoIds.forEach(id => newSelected.delete(id));
                                }
                                setSelectedVideos(newSelected);
                              }}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-white/50 rounded cursor-pointer"
                            />
                          );
                        })()}
                        <span>{releaseGroup.releaseName} ({isArtistView ? (releaseGroup.total - (releaseGroup.byStatus['archived']?.length || 0)) : (releaseCounts[releaseGroup.releaseId] || releaseGroup.total)})</span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Status groups within this release */}
                  {releaseStatusKeys.map((status) => (
                    <React.Fragment key={`${releaseGroup.releaseId}-${status}`}>
                      {/* Status Sub-Header */}
                      <tr className={getStatusHeaderClass(status as VideoSubmission['status'])}>
                        <td colSpan={artistId ? 7 : 8} className="px-6 py-1 pl-10">
                          <div className={`text-xs font-medium ${getStatusTextColor(status as VideoSubmission['status'])}`}>
                            → {getStatusLabel(status as VideoSubmission['status'])} ({releaseGroup.byStatus[status].length})
                          </div>
                        </td>
                      </tr>
                      {/* Videos in this status group */}
                      {releaseGroup.byStatus[status].map((submission) => {
                        const globalIndex = sortedSubmissions.findIndex(s => s.id === submission.id);
                        return (
                        <tr
                          key={submission.id}
                          className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedVideo(submission)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap w-12">
                            <input
                              type="checkbox"
                              checked={selectedVideos.has(String(submission.id))}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelectVideo(String(submission.id), globalIndex, e.shiftKey);
                              }}
                              onChange={() => {}}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-4 min-w-[200px]">
                            <div className="flex items-center gap-3">
                              {submission.isPhotoPost && (submission.imageUrls?.[0] || submission.videoUrl) ? (
                                <img
                                  src={submission.imageUrls?.[0] || submission.videoUrl}
                                  alt={submission.projectName}
                                  className="h-12 w-12 object-cover rounded"
                                  loading="lazy"
                                />
                              ) : submission.thumbnailUrl ? (
                                <img
                                  src={submission.thumbnailUrl}
                                  alt={submission.projectName}
                                  className="h-12 w-12 object-cover rounded"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="h-12 w-12 bg-gray-700 rounded flex items-center justify-center">
                                  {submission.videoUrl?.includes('dropbox.com') ? (
                                    <Cloud className="h-6 w-6 text-gray-400" />
                                  ) : (
                                    <Video className="h-6 w-6 text-gray-400" />
                                  )}
                                </div>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedVideo(submission);
                                }}
                                className="text-sm font-medium text-white line-clamp-2 hover:text-primary-400 transition-colors text-left flex-1"
                                title={submission.projectName}
                              >
                                {submission.isPhotoPost && (
                                  <ImageIcon className="h-3.5 w-3.5 inline mr-1 text-blue-400 flex-shrink-0" />
                                )}
                                {submission.projectName}
                              </button>
                            </div>
                          </td>
                          {!artistId && (
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-300">
                                {getArtistName(submission.artistId)}
                              </div>
                            </td>
                          )}
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300 capitalize">
                              {submission.type.replace('-', ' ')}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(submission.status)}`}>
                                {submission.status.replace(/-/g, ' ')}
                              </span>
                              {submission.status === 'ready' && submission.type === 'song-specific' && (
                                isVideoInAdCreatives(submission.videoUrl) ? (
                                  <div className="text-green-500 dark:text-green-400" title="Already in Ad Creatives">
                                    <AdTagIcon />
                                  </div>
                                ) : movingVideos.has(submission.id.toString()) ? (
                                  <div className="text-blue-500 dark:text-blue-400" title="Moving to Ad Creatives...">
                                    <Loader className="h-4 w-4 animate-spin" />
                                  </div>
                                ) : !isArtistView && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onMoveToAdCreatives(submission);
                                    }}
                                    className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                    title="Move to Ad Creatives"
                                  >
                                    <MoveRight className="h-4 w-4" />
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-300">
                              {formatDateToDDMMYY(submission.createdAt)}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {formatDateToDDMMYY(submission.updatedAt)}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedVideo(submission);
                                }}
                                className="text-white hover:text-gray-300 transition-colors"
                                title="Give Feedback"
                              >
                                <MessageSquare className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(submission.videoUrl);
                                }}
                                disabled={downloadingVideos.has(submission.videoUrl)}
                                className="text-white hover:text-gray-300 transition-colors"
                                title="Download Video"
                              >
                                {downloadingVideos.has(submission.videoUrl) ? (
                                  <Loader className="h-5 w-5 animate-spin" />
                                ) : (
                                  <Download className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // If multiple items selected, open bulk release modal
                                  if (selectedVideos.size > 1 && selectedVideos.has(String(submission.id))) {
                                    setShowBulkReleaseModal(true);
                                  } else {
                                    setEditingVideo(submission);
                                  }
                                }}
                                className="text-white hover:text-gray-300 transition-colors"
                                title="Edit Video"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchive(submission);
                                }}
                                className={`transition-colors ${
                                  submission.status === 'archived'
                                    ? 'text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300'
                                    : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                                }`}
                                title={submission.status === 'archived' ? 'Unarchive Video' : 'Archive Video'}
                              >
                                <Archive className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmation({
                                    isOpen: true,
                                    videoId: submission.id.toString(),
                                    videoName: submission.projectName,
                                  });
                                }}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                title="Delete Video"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )})}
                    </React.Fragment>
                  ))}
                  
                  {/* Archived videos within this release */}
                  {!isArtistView && releaseArchivedVideos.length > 0 && (
                    <tr className="bg-gray-500/10">
                      <td colSpan={artistId ? 7 : 8} className="px-6 py-1 pl-10">
                        <div className="text-xs font-medium text-gray-400">
                          → Archived ({releaseArchivedVideos.length})
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            /* Legacy status-only grouping when no releases */
            orderedStatusKeys.map((status) => (
              <React.Fragment key={status}>
                {/* Status Group Header */}
                <tr className={getStatusHeaderClass(status as VideoSubmission['status'])}>
                  <td colSpan={artistId ? 7 : 8} className="px-6 py-2">
                    <div className={`text-sm font-medium ${getStatusTextColor(status as VideoSubmission['status'])}`}>
                      {getStatusLabel(status as VideoSubmission['status'])} ({groupedSubmissions[status].length})
                    </div>
                  </td>
                </tr>
                {/* Videos in this status group */}
                {groupedSubmissions[status].map((submission) => {
                  const globalIndex = sortedSubmissions.findIndex(s => s.id === submission.id);
                  return (
              <tr key={submission.id} className="hover:bg-gray-800/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap w-12">
                  <input
                    type="checkbox"
                    checked={selectedVideos.has(String(submission.id))}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelectVideo(String(submission.id), globalIndex, e.shiftKey);
                    }}
                    onChange={() => {}}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap min-w-[200px]">
                  <div className="flex items-center gap-3">
                    {submission.isPhotoPost && (submission.imageUrls?.[0] || submission.videoUrl) ? (
                      <img
                        src={submission.imageUrls?.[0] || submission.videoUrl}
                        alt={submission.projectName}
                        className="h-10 w-10 object-cover rounded flex-shrink-0"
                        loading="lazy"
                      />
                    ) : submission.thumbnailUrl ? (
                      <img
                        src={submission.thumbnailUrl}
                        alt={submission.projectName}
                        className="h-10 w-10 object-cover rounded flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                        {submission.isPhotoPost ? (
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                        ) : submission.videoUrl?.includes('dropbox.com') ? (
                          <Cloud className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Video className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { console.log('[VideoList] name button clicked, opening modal for:', submission.projectName); setSelectedVideo(submission); }}
                      className="text-sm font-medium text-white truncate hover:text-primary-400 transition-colors text-left flex-1"
                      title={submission.projectName}
                    >
                      {submission.isPhotoPost && (
                        <ImageIcon className="h-3.5 w-3.5 inline mr-1 text-blue-400 flex-shrink-0" />
                      )}
                      {submission.projectName}
                    </button>
                  </div>
                </td>
                {!artistId && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      {getArtistName(submission.artistId)}
                    </div>
                  </td>
                )}
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300 capitalize">
                    {submission.type.replace('-', ' ')}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(submission.status)}`}>
                      {submission.status.replace(/-/g, ' ')}
                    </span>
                    {submission.status === 'ready' && submission.type === 'song-specific' && (
                      isVideoInAdCreatives(submission.videoUrl) ? (
                        <div
                          className="text-green-500 dark:text-green-400"
                          title="Already in Ad Creatives"
                        >
                          <AdTagIcon />
                        </div>
                      ) : movingVideos.has(submission.id.toString()) ? (
                        <div
                          className="text-blue-500 dark:text-blue-400"
                          title="Moving to Ad Creatives..."
                        >
                          <Loader className="h-4 w-4 animate-spin" />
                        </div>
                      ) : !isArtistView && (
                        <button
                          onClick={() => onMoveToAdCreatives(submission)}
                          className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                          title="Move to Ad Creatives"
                        >
                          <MoveRight className="h-4 w-4" />
                        </button>
                      )
                    )}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-300">
                    {formatDateToDDMMYY(submission.createdAt)}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-400">
                    {formatDateToDDMMYY(submission.updatedAt)}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setSelectedVideo(submission)}
                      className="text-white hover:text-gray-300 transition-colors"
                      title="Give Feedback"
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDownload(submission.videoUrl)}
                      disabled={downloadingVideos.has(submission.videoUrl)}
                      className="text-white hover:text-gray-300 transition-colors"
                      title="Download Video"
                    >
                      {downloadingVideos.has(submission.videoUrl) ? (
                        <Loader className="h-5 w-5 animate-spin" />
                      ) : (
                        <Download className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        // If multiple items selected, open bulk release modal
                        if (selectedVideos.size > 1 && selectedVideos.has(String(submission.id))) {
                          setShowBulkReleaseModal(true);
                        } else {
                          setEditingVideo(submission);
                        }
                      }}
                      className="text-white hover:text-gray-300 transition-colors"
                      title="Edit Video"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleArchive(submission)}
                      className={`transition-colors ${
                        submission.status === 'archived'
                          ? 'text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300'
                          : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                      }`}
                      title={submission.status === 'archived' ? 'Unarchive Video' : 'Archive Video'}
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmation({
                        isOpen: true,
                        videoId: submission.id.toString(),
                        videoName: submission.projectName,
                      })}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      title="Delete Video"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
                )})}
              </React.Fragment>
            ))
          )}
          
          {/* Archived Section */}
          {!isArtistView && hasArchivedVideos && (
            <>
              <tr 
                className="bg-gray-500/20 cursor-pointer hover:bg-gray-500/30 transition-colors"
                onClick={() => setShowArchived(!showArchived)}
              >
                <td colSpan={artistId ? 7 : 8} className="px-6 py-2">
                  <div className="flex items-center space-x-2 text-sm font-medium text-gray-300">
                    {showArchived ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span>Archived ({archivedVideos.length})</span>
                  </div>
                </td>
              </tr>
              
              {/* Archived Videos */}
              {showArchived && archivedVideos.map((submission) => {
                const globalIndex = sortedSubmissions.findIndex(s => s.id === submission.id);
                return (
                <tr key={submission.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap w-12">
                    <input
                      type="checkbox"
                      checked={selectedVideos.has(String(submission.id))}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectVideo(String(submission.id), globalIndex, e.shiftKey);
                      }}
                      onChange={() => {}}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[200px]">
                    <div className="flex items-center gap-3">
                      {submission.isPhotoPost && (submission.imageUrls?.[0] || submission.videoUrl) ? (
                        <img
                          src={submission.imageUrls?.[0] || submission.videoUrl}
                          alt={submission.projectName}
                          className="h-10 w-10 object-cover rounded flex-shrink-0"
                          loading="lazy"
                        />
                      ) : submission.thumbnailUrl ? (
                        <img
                          src={submission.thumbnailUrl}
                          alt={submission.projectName}
                          className="h-10 w-10 object-cover rounded flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-10 w-10 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                          {submission.isPhotoPost ? (
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          ) : submission.videoUrl?.includes('dropbox.com') ? (
                            <Cloud className="h-5 w-5 text-gray-400" />
                          ) : (
                            <Video className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedVideo(submission)}
                        className="text-sm font-medium text-white truncate hover:text-primary-400 transition-colors text-left flex-1"
                        title={submission.projectName}
                      >
                        {submission.isPhotoPost && (
                          <ImageIcon className="h-3.5 w-3.5 inline mr-1 text-blue-400 flex-shrink-0" />
                        )}
                        {submission.projectName}
                      </button>
                    </div>
                  </td>
                  {!artistId && (
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {getArtistName(submission.artistId)}
                      </div>
                    </td>
                  )}
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300 capitalize">
                      {submission.type.replace('-', ' ')}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(submission.status)}`}>
                        {submission.status.replace(/-/g, ' ')}
                      </span>
                      {submission.status === 'ready' && submission.type === 'song-specific' && (
                        isVideoInAdCreatives(submission.videoUrl) ? (
                          <div
                            className="text-green-500 dark:text-green-400"
                            title="Already in Ad Creatives"
                          >
                            <AdTagIcon />
                          </div>
                        ) : movingVideos.has(submission.id.toString()) ? (
                          <div
                            className="text-blue-500 dark:text-blue-400"
                            title="Moving to Ad Creatives..."
                          >
                            <Loader className="h-4 w-4 animate-spin" />
                          </div>
                        ) : !isArtistView && (
                          <button
                            onClick={() => onMoveToAdCreatives(submission)}
                            className="text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                            title="Move to Ad Creatives"
                          >
                            <MoveRight className="h-4 w-4" />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      {formatDateToDDMMYY(submission.createdAt)}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-400">
                      {formatDateToDDMMYY(submission.updatedAt)}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setSelectedVideo(submission)}
                        className="text-white hover:text-gray-300 transition-colors"
                        title="Give Feedback"
                      >
                        <MessageSquare className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDownload(submission.videoUrl)}
                        disabled={downloadingVideos.has(submission.videoUrl)}
                        className="text-white hover:text-gray-300 transition-colors"
                        title="Download Video"
                      >
                        {downloadingVideos.has(submission.videoUrl) ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Download className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          // If multiple items selected, open bulk release modal
                          if (selectedVideos.size > 1 && selectedVideos.has(String(submission.id))) {
                            setShowBulkReleaseModal(true);
                          } else {
                            setEditingVideo(submission);
                          }
                        }}
                        className="text-white hover:text-gray-300 transition-colors"
                        title="Edit Video"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleArchive(submission)}
                        className={`transition-colors ${
                          submission.status === 'archived'
                            ? 'text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300'
                            : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                        }`}
                        title={submission.status === 'archived' ? 'Unarchive Video' : 'Archive Video'}
                      >
                        <Archive className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirmation({
                          isOpen: true,
                          videoId: submission.id.toString(),
                          videoName: submission.projectName,
                        })}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        title="Delete Video"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )})}
            </>
          )}
        </tbody>
      </table>
      </div>
      
      {/* Pagination Controls */}
      {submissionsPagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => {
                const newPage = Math.max(1, submissionsPagination.currentPage - 1);
                fetchSubmissions(newPage, submissionsPagination.pageSize, filters, true);
              }}
              disabled={submissionsPagination.currentPage === 1 || isGenerating}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newPage = Math.min(submissionsPagination.totalPages, submissionsPagination.currentPage + 1);
                fetchSubmissions(newPage, submissionsPagination.pageSize, filters, true);
              }}
              disabled={submissionsPagination.currentPage === submissionsPagination.totalPages || isGenerating}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-medium">{(submissionsPagination.currentPage - 1) * submissionsPagination.pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(submissionsPagination.currentPage * submissionsPagination.pageSize, submissionsPagination.totalCount)}</span> of{' '}
                <span className="font-medium">{submissionsPagination.totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, submissionsPagination.currentPage - 1);
                    fetchSubmissions(newPage, submissionsPagination.pageSize, filters, true);
                  }}
                  disabled={submissionsPagination.currentPage === 1 || isGenerating}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: Math.min(submissionsPagination.totalPages, 10) }, (_, i) => {
                  let page;
                  if (submissionsPagination.totalPages <= 10) {
                    page = i + 1;
                  } else if (submissionsPagination.currentPage <= 5) {
                    page = i + 1;
                  } else if (submissionsPagination.currentPage >= submissionsPagination.totalPages - 4) {
                    page = submissionsPagination.totalPages - 9 + i;
                  } else {
                    page = submissionsPagination.currentPage - 4 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => fetchSubmissions(page, submissionsPagination.pageSize, filters, true)}
                      disabled={isGenerating}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        submissionsPagination.currentPage === page
                          ? 'z-10 bg-primary-50 dark:bg-primary-900 border-primary-500 text-primary-600 dark:text-primary-200'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => {
                    const newPage = Math.min(submissionsPagination.totalPages, submissionsPagination.currentPage + 1);
                    fetchSubmissions(newPage, submissionsPagination.pageSize, filters, true);
                  }}
                  disabled={submissionsPagination.currentPage === submissionsPagination.totalPages || isGenerating}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>

      {selectedVideo && !onSelectedVideo && (
        <FeedbackModal
          submission={selectedVideo}
          onClose={() => setSelectedVideo(null)}
          onUpdate={async () => {
            // Refresh the list with current filters, skip cache to get fresh data
            await fetchSubmissions(
              submissionsPagination.currentPage,
              submissionsPagination.pageSize,
              filters,
              true // skipCache - force fresh data
            );
          }}
        />
      )}

      {editingVideo && (
        <EditVideoModal
          submission={editingVideo}
          onClose={() => setEditingVideo(null)}
        />
      )}

      {showBulkReleaseModal && artistId && selectedSubmissions.length > 0 && (
        <BulkReleaseAssignmentModal
          submissions={selectedSubmissions}
          artistId={artistId}
          onClose={() => setShowBulkReleaseModal(false)}
          onSuccess={() => {
            setSelectedVideos(new Set());
            // Refresh submissions
            fetchSubmissions(
              submissionsPagination.currentPage,
              submissionsPagination.pageSize,
              { ...filters, artistId },
              true
            );
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title="Delete Video"
        message={`Are you sure you want to delete "${deleteConfirmation.videoName}"? This action cannot be undone and will remove all associated feedback.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmation({ isOpen: false, videoId: null, videoName: '' })}
      />

      {/* Multi-Delete Confirmation Modal */}
      {multiDeleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Multiple Videos
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You are about to delete <strong>{multiDeleteModal.count}</strong> videos. This action cannot be undone.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              To confirm, please enter the number of videos you want to delete:
            </p>
            <input
              type="number"
              placeholder={`Enter ${multiDeleteModal.count}`}
              value={multiDeleteConfirmInput}
              onChange={(e) => setMultiDeleteConfirmInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setMultiDeleteModal({ isOpen: false, count: 0 });
                  setMultiDeleteConfirmInput('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMultiDelete}
                disabled={parseInt(multiDeleteConfirmInput) !== multiDeleteModal.count || isMultiDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMultiDeleting ? (
                  <span className="flex items-center gap-2">
                    <Loader className="h-4 w-4 animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  `Delete ${multiDeleteModal.count} Videos`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};